import { useEffect, useState } from 'react';
import type { LeaderboardEntry, LiveBookState, TickerEntry } from '@hienbet/shared';
import { connectLive, emptyBook } from '../api.js';

export interface LiveState {
  book: LiveBookState;
  ticker: TickerEntry[];
  leaderboard: LeaderboardEntry[];
  balance: number | null;
  connected: boolean;
  streamDelaySeconds: number | null;
}

/**
 * Subscribes to the delayed feed.
 *
 * The book arrives as whole snapshots rather than deltas, so a dropped frame or
 * a reconnect can never leave the board subtly wrong — it is simply replaced.
 */
export function useLive(): LiveState {
  const [state, setState] = useState<LiveState>({
    book: emptyBook,
    ticker: [],
    leaderboard: [],
    balance: null,
    connected: false,
    streamDelaySeconds: null,
  });

  useEffect(() => {
    const disconnect = connectLive(
      (message) => {
        setState((prev) => {
          switch (message.type) {
            case 'hello':
              return { ...prev, streamDelaySeconds: message.streamDelaySeconds };
            case 'book':
              return { ...prev, book: message.book };
            case 'ticker':
              return { ...prev, ticker: [...message.entries, ...prev.ticker].slice(0, 25) };
            case 'leaderboard':
              return { ...prev, leaderboard: message.entries };
            case 'balance':
              return { ...prev, balance: message.balance };
            default:
              return prev;
          }
        });
      },
      (connected) => setState((prev) => ({ ...prev, connected })),
    );

    return disconnect;
  }, []);

  return state;
}

/**
 * A ticking countdown derived from the server's lock time.
 *
 * The server sends the authoritative number; this only smooths it between
 * updates so the display does not jump. It never decides anything — the lock
 * itself happens server-side.
 */
export function useCountdown(lockInSeconds: number | null): number | null {
  const [value, setValue] = useState(lockInSeconds);

  useEffect(() => {
    setValue(lockInSeconds);
    if (lockInSeconds === null) return;
    const timer = setInterval(() => {
      setValue((current) => (current === null || current <= 0 ? 0 : current - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [lockInSeconds]);

  return value;
}
